import React, { useCallback } from "react";
import { commitMutation, graphql } from "react-relay";

import { faEdit, faEllipsisV, faStar, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";

import { environment } from "~/models";
import type { ModalData as CreateGroupModalData } from "~/stream/CreateEditGroupModal";
import CreateEditGroupModal from "~/stream/CreateEditGroupModal";
import { CreateGroupMutation } from "~/stream/CreateGroupButton";
import type { ModalData as DeleteGroupModalData } from "~/stream/DeleteGroupModal";
import DeleteGroupModal from "~/stream/DeleteGroupModal";
import { useModals } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import type { HomePanelGroup_group$data as HomePanelGroup } from "~/__generated__/HomePanelGroup_group.graphql";

import styles from "./HomePanelGroupEllipsis.module.css";

const deleteGroupMutation = graphql`
	mutation HomePanelGroupEllipsis_DeleteGroupMutation($groupId: ID!) {
		removeGroup(groupId: $groupId) {
			# This returns the IDs of the deleted group and the IDs items that were in it
			# Each @deleteRecord directive is a separate publish to relay's store which causes a rerender and will cause
			# the app to crash if the group ID is returned before the item IDs.
			# To be safe we return the IDs as 1 list to avoid this situation
			deletedItems @deleteRecord
			viewer {
				homePanelGroups {
					...HomePanelGroup_group
				}
			}
		}
	}
`;

const renameGroupMutation = graphql`
	mutation HomePanelGroupEllipsis_RenameGroupMutation($groupId: ID!, $name: String!, $emoji: String) {
		editGroup(id: $groupId, name: $name, emoji: $emoji) {
			group {
				id
				name
				emojiName
			}
		}
	}
`;

type Props = {
	group: HomePanelGroup;
	triggerClassName: string;
};

export default function HomePanelGroupEllipsis(props: Props) {
	const { group, triggerClassName } = props;
	const isEditable = group.type === "REGULAR";
	const modals = useModals();

	const [isMenuOpen, setIsMenuOpen] = React.useState(false);
	const toggleOpen = React.useCallback((open: boolean) => setIsMenuOpen(open), []);

	const renameGroup = useCallback(async () => {
		const result = await modals.open<CreateGroupModalData>(({ close }) => (
			<CreateEditGroupModal initialGroup={group} onClose={close} />
		));
		if (!result) return;

		commitMutation(environment, {
			mutation: renameGroupMutation,
			variables: { groupId: group.id, name: result.name, emoji: result.emoji },
		});
	}, [modals, group]);

	const deleteGroup = useCallback(async () => {
		if (group.items.edges.length != 0) {
			const confirmDelete = await modals.open<DeleteGroupModalData>(({ close }) => (
				<DeleteGroupModal group={group} onClose={close} />
			));

			if (!confirmDelete) return;
		}

		commitMutation(environment, {
			mutation: deleteGroupMutation,
			variables: { groupId: group.id },
		});
	}, [group, modals]);

	const createGroup = useCallback(async () => {
		const result = await modals.open<CreateGroupModalData>(({ close }) => <CreateEditGroupModal onClose={close} />);
		if (!result) return;

		commitMutation(environment, {
			mutation: CreateGroupMutation,
			variables: { name: result.name, emoji: result?.emoji },
		});
	}, [modals]);

	return (
		<Menu open={isMenuOpen} onOpenChange={toggleOpen}>
			<Menu.Trigger>
				<IconButton
					icon={faEllipsisV}
					className={cx(styles.ellipsis, triggerClassName, { [styles.ellipsisVisible]: isMenuOpen })}
				></IconButton>
			</Menu.Trigger>
			<Menu.Content align="end">
				{isEditable && (
					<>
						<Menu.Item onSelect={renameGroup} icon={faEdit}>
							Rename…
						</Menu.Item>
						<Menu.Item onSelect={deleteGroup} icon={faTrashCan} destructive>
							Delete
						</Menu.Item>
						<Menu.Separator />
					</>
				)}
				<Menu.Item onSelect={createGroup} icon={faStar}>
					Create new group…
				</Menu.Item>
			</Menu.Content>
		</Menu>
	);
}
